'use strict';

import Validator from '../../../helpers/validate';
import FormTracker from '../../../services/form-tracker';

export default class Form {
    constructor(rules) {
        this.fields = {};
        this.rules = rules;
        this.validationCallbacks = [];
        this.changeCallbacks = [];
        this.pendingValidations = Promise.resolve(true);
        this.initValidators();
        this.attempted = false;
        this.attemptedCallbacks = [];
        this.formTracker = FormTracker;
    }

    trim(fieldName) {
        const field = this.fields[fieldName];
        if (!field || typeof field.value !== 'string') {
            return;
        }
        const value = field.value;
        if (typeof value !== 'string') {
            return;
        }
        // eslint-disable-next-line better-mutation/no-mutation
        field.value = value.trim();
    }

    onFirstAttempt(callback) {
        if (this.attempted) {
            return false;
        } else {
            this.attemptedCallbacks.push(callback);
            return true;
        }
    }

    attempt(field) {
        this.enableValidation(field);
        if (!this.attempted) {
            this.attempted = true;
            this.attemptedCallbacks.forEach((cb) => cb());
        }
    }

    setFromGenerated(field, value) {
        this._set(field, value, false);
        this.enableValidation(field);
    }

    setFromUserInput(field, value) {
        this._set(field, value, true);
    }

    _set(field, value, isSetFromUserInput = true) {
        if (value === this.get(field)) {
            return;
        }
        this.updateField(field, {
            value: value,
            isValidationPending: true,
            isSetFromUserInput: isSetFromUserInput,
            errors: []
        });
        const validation = this.validate(field).then((result) => {
            const currentValue = this.get(field);
            if (result.value !== currentValue) {
                return;
            }
            this.updateField(field, {
                isValid: result.isValid,
                errors: result.errors,
                isValidationPending: false
            });
            this.triggerOnValidation(field);
            return;
        });
        this.pendingValidations = Promise.all([
            this.pendingValidations,
            validation
        ]);
    }

    get(field) {
        const f = this.fields[field];
        return f ? f.value : null;
    }

    remove(field) {
        delete this.fields[field];
        this.triggerOnChange(field);
    }

    update(action) {
        action(this);
        this.triggerOnChange(null);
    }

    addError(field, error) {
        this.updateField(field, {
            errors: [error],
            isValid: false
        });
        this.triggerOnChange(field);
    }

    fieldNames() {
        return Object.keys(this.fields);
    }

    updateField(field, update) {
        this.fields = {
            ...this.fields,
            [field]: {
                ...this.fields[field],
                ...update
            }
        };
        this.triggerOnChange(field);
    }

    waitForValidation(callback, fields) {
        const fieldNames = fields || this.fieldNames();
        fieldNames.forEach((x) => {
            this.enableValidation(x);
        });
        // eslint-disable-next-line
        this.pendingValidations.then(callback).catch(() => {});
    }

    /** @param {string[]} fieldNames */
    isValid(fieldNames) {
        const fieldsToCheck = fieldNames || this.fieldNames();
        for (const field of fieldsToCheck) {
            if (!this.fields[field] || !this.fields[field].isValid) {
                return false;
            }
        }
        return true;
    }

    hasError(fieldName, errorCode) {
        const field = this.fields[fieldName];
        if (!field || !field.errors || field.errors.length === 0) {
            return false;
        }
        return field.errors.some((x) => x.code === errorCode);
    }

    /**
     * Returns true if any of the fields in the array has been set from user input.
     * @param {string[]} fieldNames
     */
    hasAnyFieldSetFromUserInput(fieldNames) {
        const fieldsToCheck = fieldNames || this.fieldNames();
        for (const field of fieldsToCheck) {
            if (this.fields[field] && this.fields[field].isSetFromUserInput) {
                return true;
            }
        }
        return false;
    }

    enableValidation(field) {
        this.updateField(field, {
            isValidationEnabled: true
        });
    }

    onChange(callback) {
        this.changeCallbacks.push(callback);
    }

    removeOnChange(callback) {
        this.changeCallbacks = this.changeCallbacks.filter(
            (x) => x !== callback
        );
    }

    triggerOnChange(field) {
        this.changeCallbacks.forEach((c) => c(this, field));
    }

    onValidation(callback) {
        this.validationCallbacks.push(callback);
    }

    removeOnValidation(callback) {
        this.validationCallbacks = this.validationCallbacks.filter(
            (x) => x !== callback
        );
    }

    triggerOnValidation(field) {
        this.validationCallbacks.forEach((c) => c(this, field));
    }

    initValidators() {
        Object.keys(this.rules).forEach((field) => {
            const rule = this.rules[field];
            // eslint-disable-next-line better-mutation/no-mutation
            rule.validate = () => {
                const value = this.get(field);
                return Validator(value, rule.rules, rule.async).then(
                    (result) => ({
                        field: field,
                        value: value,
                        ...result
                    })
                );
            };
        });
    }

    validate(field) {
        const value = this.get(field);
        const rule = this.rules[field];
        if (!rule) {
            return new Promise((resolve) =>
                resolve({
                    field: field,
                    value: value,
                    isValid: true
                })
            );
        }
        return rule.validate();
    }
}
